import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import styles from './GeneralContractInfoSubmitForm.module.scss'
import { useForm, Controller } from 'react-hook-form'
import ToggleButtonGroupController from '../../components/ToggleButtonGroup/ToggleButtonGroupController'
import SimpleInputController from '../../components/simpleInput/SimpleInputController'
import IncentiveModal from '../onboarding/IncentiveModal'
import { formatIncentiveTaxOptionLabel } from '../../utils/incentiveDisplay'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'

const GeneralContractInfoSubmitForm = forwardRef(({ onValidationChange, isOverseas }, ref) => {
	const { control, handleSubmit, watch, setValue, formState } = useForm({
		defaultValues: {
			general: {
				applyTax: 'exclude', // 세전 (왼쪽 버튼)
				payPeriod: 'weekly', // 주급 (왼쪽 버튼)
				periodSalary: '', // 기간급여액
				sponsorCap: true, // 착용 (왼쪽 버튼)
				overseasTour: false, // 해외 대회 포함 여부 (왼쪽 버튼)
				overseasSupport: 0, // 해외대회 지원 항목
				incentiveSettings: [], // 인센티브 설정
				incentiveTaxOption: 'amountDisplayed', // 인센티브 택스 옵션
			},
		},
	})

	useEffect(() => {
		if (isOverseas) {
			setValue('general.overseasTour', true)
		}
	}, [isOverseas])
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [isDropdownItemSelected, setIsDropdownItemSelected] = useState(false)
	const dropdownRef = useRef(null)

	const formatNumber = (value) => {
		if (value === null || value === undefined) return ''
		return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	}

	// GeneralContractPreviousData: 기간계약(Term) API 응답 또는 동일 형태
	useEffect(() => {
		try {
			const previousData = sessionStorage.getItem('GeneralContractPreviousData')
			if (!previousData) return
			const parsed = JSON.parse(previousData)
			if (parsed.salaryAmountForPeriod === undefined) {
				if (parsed.overseasSupport !== undefined && parsed.overseasTour === true) {
					setIsDropdownItemSelected(true)
				}
				return
			}
			setValue('general.applyTax', parsed.isSalaryTaxIncluded ? 'include' : 'exclude')
			setValue('general.payPeriod', parsed.payrollCycles === 'weekly' ? 'weekly' : 'oneTime')
			setValue('general.periodSalary', formatNumber(parsed.salaryAmountForPeriod))
			setValue('general.sponsorCap', parsed.wearSponsoredHat)
			setValue('general.overseasTour', parsed.isOverseasIncluded)
			if (
				parsed.isOverseasIncluded &&
				parsed.expenseReimbursement !== undefined &&
				parsed.expenseReimbursement !== null
			) {
				setValue('general.overseasSupport', parsed.expenseReimbursement)
				setIsDropdownItemSelected(true)
			}
			setValue(
				'general.incentiveSettings',
				(parsed.incentives || [])
					.slice()
					.sort((a, b) => a.startRank - b.startRank)
					.map((setting, index) => ({
						id: index + 1,
						start: setting.startRank,
						end: setting.endRank,
						percentage: setting.percentage,
					})),
			)
			setValue(
				'general.incentiveTaxOption',
				parsed.prizeType === 'displayed' ? 'amountDisplayed' : 'amountReceived',
			)
		} catch (error) {
			console.error('GeneralContractPreviousData 로드 실패:', error)
		}
	}, [setValue])

	// 토글 옵션들
	const applyTaxOption = [
		{ value: 'exclude', label: '세전' },
		{ value: 'include', label: '세후' },
	]

	const payPeriodOption = [
		{ value: 'weekly', label: '주급' },
		{ value: 'oneTime', label: '일시불' },
	]

	const sponsorCapOption = [
		{ value: true, label: '착용' },
		{ value: false, label: '미착용' },
	]

	const overseasTourOption = [
		{ value: false, label: '미포함(국내만)' },
		{ value: true, label: '해외대회 포함' },
	]

	// isOverseas가 true일 때는 해외대회 포함만 보이도록 필터링
	const filteredOverseasTourOption = isOverseas
		? overseasTourOption.filter((option) => option.value === true)
		: overseasTourOption

	const overseasSupportOptions = [
		{ value: 0, label: '미지원' },
		{ value: 10, label: '10%' },
		{ value: 30, label: '30%' },
		{ value: 50, label: '50%' },
		{ value: 70, label: '70%' },
		{ value: 90, label: '90%' },
		{ value: 100, label: '100%' },
	]

	// 드롭다운 토글 함수
	const toggleDropdown = () => {
		setIsDropdownOpen((prev) => !prev)
	}

	// 드롭다운 아이템 선택 함수
	const handleItemSelect = (e, value) => {
		console.log('value', value)
		e.stopPropagation()
		setValue('general.overseasSupport', value)
		setIsDropdownItemSelected(true)
		setIsDropdownOpen(false)
	}

	// 현재 폼 데이터
	const activeData = watch('general')
	const isOverseasTour = watch('general.overseasTour')
	const overseasSupport = watch('general.overseasSupport')
	const incentiveSettings = watch('general.incentiveSettings')

	// validation 상태를 상위 컴포넌트로 전달
	useEffect(() => {
		const isValid =
			formState.isValid &&
			incentiveSettings &&
			incentiveSettings.length > 0 &&
			(isOverseasTour === false || (isOverseasTour === true && isDropdownItemSelected))

		onValidationChange && onValidationChange('general', isValid)
	}, [formState.isValid, incentiveSettings, isOverseasTour, isDropdownItemSelected])

	// 가격 입력 처리 함수
	const handlePriceInputChange = (e, fieldOnChange) => {
		// 숫자만 입력받도록 처리
		const numericValue = e.target.value.replace(/[^0-9]/g, '')

		// 천원 단위 콤마 적용
		const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

		// 필터링된 값으로 폼 업데이트
		fieldOnChange(formattedValue)
	}

	// 모달 열기 함수
	const handleModalOpen = () => {
		setIsModalOpen(true)
	}

	// 드롭다운 외부 클릭 감지
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false)
			}
		}

		if (isDropdownOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isDropdownOpen])

	// 폼 제출 처리
	const onSubmit = (data) => {
		console.log('폼 데이터:', data)
		// 여기에 제출 로직 추가
	}

	// ref를 통해 부모 컴포넌트에서 호출할 수 있는 메서드들
	useImperativeHandle(ref, () => ({
		getFormData: () => {
			return {
				applyTax: activeData?.applyTax,
				payPeriod: activeData?.payPeriod,
				periodSalary: activeData?.periodSalary
					? activeData.periodSalary.replace(/,/g, '')
					: '',
				sponsorCap: activeData?.sponsorCap,
				overseasTour: activeData?.overseasTour,
				overseasSupport: activeData?.overseasSupport,
				incentiveSettings: activeData?.incentiveSettings,
				incentiveTaxOption: activeData?.incentiveTaxOption,
			}
		},
	}))

	return (
		<div className={styles.contractInfoSubmitFormWrapper}>
			<span className={styles.contractInfoSubmitFormTitle}>계약 정보를 작성해주세요</span>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className={styles.contractInfoSubmitFormContainer}>
				<div className={styles.applyTax}>
					<ToggleButtonGroupController
						control={control}
						name="general.applyTax"
						toggleFieldLabel="임금 세금 포함 여부"
						toggleOption={applyTaxOption}
						isNoticeNeed={true}
						isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
						styleMode={styles}
					/>
				</div>
				<div className={styles.payPeriod}>
					<ToggleButtonGroupController
						control={control}
						name="general.payPeriod"
						toggleFieldLabel="임금 지급 주기"
						toggleOption={payPeriodOption}
						styleMode={styles}
					/>
				</div>
				<div className={styles.periodSalary}>
					<SimpleInputController
						control={control}
						name="general.periodSalary"
						rules={{ required: '기간급여액을 입력해주세요' }}
						inputLabel="기간급여액"
						inputPlaceholder="기간급여액을 입력해주세요"
						onChange={handlePriceInputChange}
					/>
				</div>
				<div className={styles.incentive}>
					<div className={styles.incentiveInfo}>
						인센티브
						<InformationCircleIcon
							className={styles.noticeIcon}
							data-tooltip-id="my-tooltip10"
							data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
						/>
						<Tooltip id="my-tooltip10" place="top" className={styles.tooltip} />
					</div>
					<button type="button" onClick={handleModalOpen}>
						입력하기
					</button>
				</div>

				<Controller
					name="general.incentiveSettings"
					control={control}
					render={({ field }) => <input type="hidden" {...field} />}
				/>
				{activeData?.incentiveSettings && activeData?.incentiveSettings.length > 0 && (
					<div className={styles.incentiveDisplaySection}>
						<div className={styles.incentiveTaxDisplay}>
							{formatIncentiveTaxOptionLabel(activeData.incentiveTaxOption)}
						</div>
						{activeData.incentiveSettings.map((setting) => {
							if (setting.percentage === null && setting.end === null) return null
							const displayRank =
								setting.id === 1
									? '우승 : '
									: setting.start +
										(setting.end !== null ? ` ~ ${setting.end} :  ` : '~ ')
							const displayPercentage =
								setting.percentage !== null ? `  ${setting.percentage}%` : '미입력'
							return (
								<div key={setting.id} className={styles.incentiveDisplayItem}>
									<div>{displayRank}</div>

									<div>{displayPercentage}</div>
								</div>
							)
						})}
					</div>
				)}
				<div className={styles.overseasTour}>
					<ToggleButtonGroupController
						control={control}
						name="general.overseasTour"
						toggleFieldLabel="해외 대회 포함 여부"
						toggleOption={overseasTourOption}
						styleMode={styles}
						disabledOptions={isOverseas ? [false] : []}
					/>
				</div>
				{isOverseasTour && (
					<div className={styles.overSeasSupport}>
						<div className={styles.overSeasSupportTitle}>해외대회 지원 항목</div>
						<div className={styles.overSeasSupportContainer} ref={dropdownRef}>
							<div
								className={styles.overSeasSupportDropDownInput}
								// ref={dropdownRef}
								onClick={toggleDropdown}
								role="button"
								aria-haspopup="listbox"
								aria-expanded={isDropdownOpen}>
								{!isDropdownItemSelected ? (
									<span className={styles.overSeasSupportDropDownPlaceholder}>
										해외대회 지원 항목을 선택해주세요
									</span>
								) : (
									<span className={styles.overSeasSupportDisplay}>
										{overseasSupport === 0 ? '미지원' : overseasSupport + '%'}
									</span>
								)}
								<ChevronDownIcon className={styles.dropDownArrow} />
							</div>
							{isDropdownOpen && (
								<ul
									className={`${styles.dropDownList} ${isDropdownOpen ? styles.dropDownListOpened : styles.dropDownListClosed}`}>
									{overseasSupportOptions.map((option) => (
										<li
											key={option.value}
											className={styles.dropDownListItem}
											onClick={(e) => handleItemSelect(e, option.value)}>
											{option.label}
										</li>
									))}
								</ul>
							)}
						</div>
					</div>
				)}
				<div className={styles.sponsorCap}>
					<ToggleButtonGroupController
						control={control}
						name="general.sponsorCap"
						toggleFieldLabel="선수 스폰서 모자 착용"
						toggleOption={sponsorCapOption}
						styleMode={styles}
					/>
				</div>
			</form>

			{/* 인센티브 모달 */}
			<IncentiveModal
				isModalOpen={isModalOpen}
				onFinish={(incentiveTaxOption, incentiveSettings) => {
					setValue('general.incentiveSettings', incentiveSettings)
					setValue('general.incentiveTaxOption', incentiveTaxOption)
					setIsModalOpen(false)
				}}
				onReturn={() => setIsModalOpen(false)}
				initialSettings={activeData?.incentiveSettings}
			/>
		</div>
	)
})

export default GeneralContractInfoSubmitForm
